import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
} from '@mui/material';
import propertyService, { Property } from '../../services/propertyService';

interface PropertyRecommendationDialogProps {
  open: boolean;
  onClose: () => void;
  reservationId: number;
  currentProperty: Property;
  onSuccess?: () => void;
}

const PropertyRecommendationDialog: React.FC<PropertyRecommendationDialogProps> = ({
  open,
  onClose,
  reservationId,
  currentProperty,
  onSuccess,
}) => {
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoadingProperties(true);
        const data = await propertyService.getProperties();
        // Filter out the current property from the list
        setProperties(data.filter(prop => prop.id !== currentProperty.id));
      } catch (err) {
        setError('Failed to load properties');
        console.error('Error loading properties:', err);
      } finally {
        setLoadingProperties(false);
      }
    };

    if (open) {
      fetchProperties();
    }
  }, [open, currentProperty.id]);

  const handleSubmit = async () => {
    if (!selectedProperty) {
      setError('Please select a property to recommend');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await propertyService.sendRecommendation({
        reservation_id: reservationId,
        recommended_property_id: selectedProperty.id,
        message: message,
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send recommendation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Recommend Alternative Property</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Autocomplete
          options={properties}
          getOptionLabel={(option) => `${option.name} (${option.property_id})`}
          value={selectedProperty}
          onChange={(_, newValue) => setSelectedProperty(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Property"
              margin="normal"
              fullWidth
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingProperties ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(options, { inputValue }) =>
            options.filter(
              (option) =>
                option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                option.property_id.toLowerCase().includes(inputValue.toLowerCase())
            )
          }
        />

        <TextField
          label="Message (Optional)"
          multiline
          rows={4}
          fullWidth
          margin="normal"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Add a personal message to the guest..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !selectedProperty}
        >
          {loading ? <CircularProgress size={24} /> : 'Send Recommendation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PropertyRecommendationDialog; 